import React from "react";

export default function TermsAndConditions() {
  return (
    <div className="bg-light">
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-9 col-xl-8">
            <div className="card shadow-sm border-0">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex flex-column flex-md-row justify-content-between gap-3 mb-4">
                  <div>
                    <h1 className="h3 mb-2">Terms and Conditions</h1>
                    <p className="text-muted mb-0">
                      These Terms govern the use of this counselling directory platform.
                    </p>
                  </div>
                  <div className="text-md-end">
                    <span className="badge text-bg-secondary">
                      Last updated: 26 February 2026
                    </span>
                  </div>
                </div>

                <hr className="my-4" />

                <h2 className="h5">1. Acceptance of Terms</h2>
                <p className="mb-4">
                  By accessing or using this website or application, you agree to be bound by these
                  Terms and Conditions. If you do not agree, you must not use the platform.
                </p>

                <h2 className="h5">2. Purpose of the Platform</h2>
                <p className="mb-2">
                  This platform operates solely as a counselling directory and enquiry system.
                  It may only be used in one of the following two ways:
                </p>
                <ul className="mb-4">
                  <li>As an individual searching for a counsellor or therapist</li>
                  <li>As a counsellor or therapist creating and managing a professional profile</li>
                </ul>
                <p className="mb-4">
                  Any other use of the platform is strictly prohibited.
                </p>

                <h3 className="h6">Scope of the Directory</h3>
                <p className="mb-4">
                  This directory focuses on counsellors and therapists who describe their practice
                  as affirming or specialist in specific areas of counselling or therapy. These
                  descriptions reflect the counsellor’s stated areas of focus, training, or
                  professional interest.
                </p>

                <h2 className="h5">3. Counsellor Accounts and Profile Information</h2>
                <p className="mb-2">
                  Counsellors and therapists are responsible for all information submitted to the
                  platform.
                </p>
                <ul className="mb-4">
                  <li>All information must be accurate, truthful, and not misleading</li>
                  <li>Qualifications, licences, registrations, and awarding bodies must be genuine</li>
                  <li>Professional titles must reflect actual credentials</li>
                  <li>Awards and accreditations must be real and verifiable</li>
                  <li>Outdated or incorrect information must be updated promptly</li>
                </ul>
                <p className="mb-4">
                  We reserve the right to remove, suspend, or modify profiles that contain false,
                  misleading, or unverifiable information.
                </p>

                <h2 className="h5">4. Verification of Counsellor Information</h2>
                <p className="mb-4">
                  We verify counsellor credentials by reviewing submitted licence details,
                  registrations, or awarding body information. Verification is limited to confirming
                  that provided details correspond to recognised licensing or awarding bodies at the
                  time of review.
                </p>
                <p className="mb-4">
                  Verification does not constitute endorsement, accreditation, or a guarantee of
                  service quality. Counsellors remain fully responsible for maintaining valid
                  credentials and complying with all professional and legal requirements applicable
                  to their practice.
                </p>

                <h3 className="h6">Affirming and Specialist Descriptions</h3>
                <p className="mb-4">
                  Terms such as affirming, specialist, or similar descriptors are used to reflect how
                  counsellors describe their own practice and professional focus. While licences and
                  awarding bodies are verified, these descriptors do not constitute clinical
                  guarantees, endorsements, or assurances of outcomes.
                </p>

                <h2 className="h5">5. Enquiries and Communication</h2>
                <p className="mb-2">
                  Users may send enquiries to counsellors through the platform for the purpose of
                  seeking counselling services only.
                </p>
                <ul className="mb-4">
                  <li>Enquiries are stored so counsellors can read and respond within the app</li>
                  <li>Users are responsible for the content they submit</li>
                  <li>Counsellors are responsible for their responses and communications</li>
                </ul>

                <h2 className="h5">6. Use of Counsellor Contact Details</h2>
                <p className="mb-2">
                  Contact details displayed on counsellor profiles may only be used for legitimate
                  counselling related enquiries.
                </p>
                <ul className="mb-4">
                  <li>Contacting counsellors for marketing, sales, or promotions is prohibited</li>
                  <li>Bulk messaging, scraping, or harvesting contact details is prohibited</li>
                  <li>Using contact details for harassment or unsolicited contact is prohibited</li>
                  <li>Using contact details for any non counselling purpose is prohibited</li>
                </ul>
                <p className="mb-4">
                  Misuse of contact information may result in immediate suspension or banning from
                  the platform.
                </p>

                <h2 className="h5">7. Prohibited Use and Conduct</h2>
                <p className="mb-2">
                  The platform must be used respectfully, lawfully, and in a manner appropriate for a
                  professional counselling environment. Users and counsellors must not:
                </p>
                <ul className="mb-4">
                  <li>Use the platform for any unlawful or criminal purpose</li>
                  <li>Post, upload, transmit, or link to offensive, abusive, threatening, or harassing content</li>
                  <li>Post or share explicit, sexual, pornographic, or sexually suggestive images or material</li>
                  <li>Post content that is hateful, discriminatory, or degrading based on any protected characteristic</li>
                  <li>Use obscene, excessively violent, or inappropriate language</li>
                  <li>Promote or depict criminal activity, self harm, or abuse</li>
                  <li>Upload images or content that are not relevant to professional counselling services</li>
                  <li>Impersonate another individual or misrepresent professional status</li>
                  <li>Attempt to bypass platform security or access restricted areas</li>
                  <li>Use automated tools to scrape, harvest, or extract data</li>
                </ul>
                <p className="mb-4">
                  We reserve the right to remove content, suspend accounts, or permanently ban users
                  or counsellors who breach these standards.
                </p>

                <h2 className="h5">8. No Medical or Crisis Services</h2>
                <p className="mb-4">
                  The platform does not provide emergency, crisis, or medical services. Counsellors
                  listed operate independently. Users experiencing an emergency should contact
                  appropriate emergency or crisis services.
                </p>

                <h2 className="h5">9. Liability and Disclaimer</h2>
                <p className="mb-2">
                  The platform acts only as an intermediary directory.
                </p>
                <ul className="mb-4">
                  <li>We are not responsible for counselling outcomes</li>
                  <li>We are not responsible for advice or services provided by counsellors</li>
                  <li>We are not responsible for disputes between users and counsellors</li>
                </ul>
                <p className="mb-4">
                  Use of the platform is at your own risk.
                </p>

                <h2 className="h5">10. Account Suspension and Termination</h2>
                <p className="mb-4">
                  We reserve the right to suspend or terminate access to the platform at any time,
                  with or without notice, if these Terms are violated or if continued access poses a
                  risk to the platform or its users.
                </p>

                <h2 className="h5">11. Data and Privacy</h2>
                <p className="mb-4">
                  Personal data is handled in accordance with our Privacy Policy. By using the
                  platform, you acknowledge and agree to those practices.
                </p>

                <h2 className="h5">12. Changes to These Terms</h2>
                <p className="mb-4">
                  We may update these Terms from time to time. Continued use of the platform after
                  changes are made constitutes acceptance of the updated Terms.
                </p>

                <h2 className="h5">13. Governing Law</h2>
                <p className="mb-0">
                  These Terms are governed by and interpreted in accordance with applicable laws in
                  the jurisdiction in which the platform operates.
                </p>

                <hr className="my-4" />

                <p className="text-muted small mb-0">
                  These Terms are intended to protect users, counsellors, and the integrity of the
                  platform.
                </p>
              </div>
            </div>

            <div className="text-center mt-4">
              <a href="/" className="btn btn-outline-secondary">
                Back to home
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}